import { useBilling } from './hooks/useBilling';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '@/stores/authStore';
import { useState } from 'react';

export default function RateFixation() {
  const { setRate, rates } = useBilling();
  const { user } = useAuthStore();
  const { register, handleSubmit, formState: { isSubmitting }, reset } = useForm();
  const [showHistory, setShowHistory] = useState(false);

  const onSubmit = async (data: any) => {
    try {
      await setRate.mutateAsync({
        ...data,
        rate: parseFloat(data.rate),
        lockedBy: user?.name || 'Admin'
      });
      reset();
    } catch (error) {
      console.error('Failed to set rate:', error);
    }
  };

  if (rates.isLoading) return <div className="p-6">Loading rates...</div>;

  const currentRates = rates.data?.filter((r: any) => r.isActive) || [];
  const historicalRates = rates.data?.filter((r: any) => !r.isActive) || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Rate Fixation</h1>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="btn-primary"
        >
          {showHistory ? 'Hide History' : 'View History'}
        </button>
      </div>

      {/* Rate Setting Form */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Set New Rate</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="grid md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Metal Type</label>
            <select className="input" {...register('metal', { required: true })}>
              <option value="">Select Metal</option>
              <option value="Gold">Gold</option>
              <option value="Silver">Silver</option>
              <option value="Platinum">Platinum</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Rate per gram (₹)</label>
            <input
              className="input"
              type="number"
              step="0.01"
              placeholder="Rate/gm"
              {...register('rate', { required: true, min: 0 })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Effective Date</label>
            <input
              className="input"
              type="date"
              {...register('effectiveDate', { required: true })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Locked By</label>
            <input
              className="input"
              type="text"
              value={user?.name || 'Admin'}
              readOnly
              disabled
            />
          </div>
          
          <div className="flex items-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full whitespace-nowrap"
            >
              {isSubmitting ? 'Saving...' : 'Lock Rate'}
            </button>
          </div>
        </form>
      </div>

      {/* Current Active Rates */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Current Active Rates</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/20">
              <tr>
                <th className="th">Metal</th>
                <th className="th">Rate/gm (₹)</th>
                <th className="th">Effective Date</th>
                <th className="th">Locked By</th>
                <th className="th">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentRates.length > 0 ? (
                currentRates.map((rate: any) => (
                  <tr key={rate.id} className="tr">
                    <td className="td">
                      <span className="font-medium">{rate.metal}</span>
                    </td>
                    <td className="td">
                      <span className="text-[var(--color-primary)] font-semibold">
                        ₹{rate.rate.toLocaleString()}
                      </span>
                    </td>
                    <td className="td">{new Date(rate.effectiveDate).toLocaleDateString()}</td>
                    <td className="td">{rate.lockedBy}</td>
                    <td className="td">
                      <span className="badge approved">Active</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="td text-center text-gray-500">
                    No active rates found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historical Rates */}
      {showHistory && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Rate History</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="th">Metal</th>
                  <th className="th">Rate/gm (₹)</th>
                  <th className="th">Effective Date</th>
                  <th className="th">Locked By</th>
                  <th className="th">Created</th>
                </tr>
              </thead>
              <tbody>
                {historicalRates.length > 0 ? (
                  historicalRates.map((rate: any) => (
                    <tr key={rate.id} className="tr">
                      <td className="td">{rate.metal}</td>
                      <td className="td">₹{rate.rate.toLocaleString()}</td>
                      <td className="td">{new Date(rate.effectiveDate).toLocaleDateString()}</td>
                      <td className="td">{rate.lockedBy}</td>
                      <td className="td">{new Date(rate.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="td text-center text-gray-500">
                      No historical rates found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}